<template>
  <div>
    <button
      v-if="!href"
      :disabled="disabled"
      :aria-disabled="disabled || showLoading"
      :aria-busy="showLoading"
      :aria-label="ariaLabel"
      :aria-describedby="ariaDescribedBy"
      :class="classes"
      :type="buttonType"
      @click="!showLoading ? onDoClick() : undefined"
    >
      <div
        v-if="label"
        class="absolute top-90 left-[24rem] bg-surface-action-2 px-4 py-1 text-14-18"
      >
        {{ label }}
      </div>

      <template v-if="showLoading">
        <div class="w-full">
          <div class="w-full flex justify-center">
            <nuxt-icon
              class="[&>*]:animate-spin-slow-ease [&>*]:motion-reduce:animate-none"
              name="refresh"
              :filled="false"
              aria-label="laddningssymbol"
            />
          </div>
          <div class="invisible h-0">
            <slot></slot>
          </div>
        </div>
      </template>

      <template v-if="!showLoading">
        <slot></slot>
      </template>
    </button>

    <NuxtLink
      v-if="href"
      :to="href"
      external
      class="focus-rounded"
      :class="classes"
      :aria-label="ariaLabel"
      :target="linkTarget"
    >
      <slot></slot>
    </NuxtLink>
  </div>
</template>

<script lang="ts" setup>
export type ButtonType = 'button' | 'submit' | 'reset';
export type ButtonSize = 'default' | 'sm' | 'responsive';

interface Props {
  href?: string;
  color?: 'primary' | 'secondary' | 'seasonaldeals';
  size?: ButtonSize;
  disabled?: boolean;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  buttonType?: ButtonType;
  showLoading?: boolean;
  fullWidth?: boolean;
  squared?: boolean;
  gtagEventName?: string;
  label?: string;
  linkTarget?: string;
  shadow?: 'defaultButton' | 'soft' | 'softWider' | 'blue' | 'none';
  noBorder?: boolean;
  iconName?: string;
  iconPos?: 'left' | 'right';
}

const props = withDefaults(defineProps<Props>(), {
  href: undefined,
  color: 'primary',
  size: 'default',
  disabled: false,
  ariaLabel: undefined,
  ariaDescribedBy: undefined,
  buttonType: 'submit',
  showLoading: false,
  fullWidth: false,
  squared: false,
  gtagEventName: '',
  label: '',
  linkTarget: '_self',
  noBorder: false,
  iconName: '',
  iconPos: 'right',
  shadow: 'defaultButton',
});

const emit = defineEmits(['onClick']);

const onDoClick = () => {
  emit('onClick');
};

const paddingClass = computed(() => {
  if (props.size === 'sm') {
    return 'p-8';
  }

  return 'p-16';
});

const heightClass = computed(() => {
  if (props.size === 'sm') {
    return 'h-44';
  }

  if (props.size === 'responsive') {
    return 'h-44 md:h-56';
  }

  return 'h-56';
});

const fontSizeClass = computed(() => {
  if (props.size === 'sm') {
    return 'text-18';
  }

  return 'text-24';
});

const widthClass = computed(() => {
  if (props.fullWidth) {
    return 'w-full';
  }

  if (props.squared) {
    if (props.size === 'sm') {
      return 'w-44';
    }
    if (props.size === 'responsive') {
      return 'w-44 md:w-56';
    }
    return 'w-56';
  }

  return 'w-fit';
});

const classes = computed(
  () => `
  ${paddingClass.value}
  ${heightClass.value}
  gap-16
  cursor-pointer
  flex
  justify-center
  items-center
  hover:bg-surface-action-hover
  hover:text-text-on-action
  ${props.noBorder ? 'border-none' : 'border-2 border-solid border-border-primary'}
  rounded
  ${
    props.shadow === 'soft'
      ? 'shadow-buttonSoft'
      : props.shadow === 'softWider'
        ? 'shadow-buttonSoftWider'
        : props.shadow === 'blue'
          ? 'shadow-buttonBlue'
          : props.shadow === 'defaultButton'
            ? 'shadow-button'
            : props.shadow === 'none'
              ? ''
              : ''
  }
  ${fontSizeClass.value}
  leading-[2.64rem]
  disabled:bg-surface-disabled
  disabled:text-text-on-disabled
  disabled:border-border-disabled
  disabled:shadow-none
  no-underline
  ${widthClass.value}
  ${
    props.color === 'primary'
      ? 'bg-surface-action-1 text-text-on-highlight'
      : props.color === 'secondary'
        ? 'bg-surface-action-2 text-text-primary'
        : props.color === 'seasonaldeals'
          ? 'bg-surface-primary dark:bg-surface-warning text-text-on-highlight'
          : ''
  }
`,
);
</script>

<style scoped lang="scss"></style>
